import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

const PERIODS = [
  ["all", "All time"],
  ["today", "Today"],
  ["7", "7 days"],
  ["30", "30 days"],
  ["month", "This month"],
  ["custom", "Custom"],
];

const STATUSES = [
  ["all", "All unpaid"],
  ["pending", "Pending"],
  ["failed", "Failed"],
  ["processing", "Processing"],
];

function money(amount) {
  const value = Number(amount) || 0;
  return `₦${value.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function personName(person) {
  return [person?.firstName, person?.lastName].filter(Boolean).join(" ") || person?.username || "User";
}

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function periodRange(period, customFrom, customTo) {
  const now = new Date();
  if (period === "today") return { from: startOfDay(now).toISOString() };
  if (period === "7" || period === "30") {
    const from = startOfDay(now);
    from.setDate(from.getDate() - (period === "7" ? 6 : 29));
    return { from: from.toISOString() };
  }
  if (period === "month") {
    return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString() };
  }
  if (period === "custom") {
    const range = {};
    if (customFrom) range.from = new Date(customFrom).toISOString();
    if (customTo) range.to = customTo;
    return range;
  }
  return {};
}

const PaymentsAdmin = () => {
  const [kind, setKind] = useState("sale");
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [secret, setSecret] = useState("");
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);

  const load = useCallback(async (nextKind = kind) => {
    const params = {
      kind: nextKind,
      paymentStatus: paymentStatus === "all" ? undefined : paymentStatus,
      ...periodRange(period, customFrom, customTo),
    };
    const [payments, config] = await Promise.all([
      api.get("/admin/payments", { params }),
      api.get("/admin/platform/settings"),
    ]);
    setRows(payments.data.data.rows || []);
    setSettings(config.data.data.settings);
  }, [kind, paymentStatus, period, customFrom, customTo]);

  useEffect(() => {
    load().catch(() => setError("Could not load payments."));
  }, [load]);

  useEffect(() => {
    setSelected([]);
  }, [kind, period, paymentStatus, customFrom, customTo, search]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => {
      const person = kind === "delivery" ? row.agent : row.user;
      const bank = kind === "delivery" ? row.selectedPaymentBank : row.bankDetails;
      return `${personName(person)} ${person?.phoneNumber || ""} ${person?.email || ""} ${bank?.accountNumber || ""} ${bank?.bankName || ""} ${bank?.accountName || ""}`
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, search, kind]);

  const selectedRows = visible.filter((row) => selected.includes(row._id));
  const selectedTotal = selectedRows.reduce(
    (sum, row) => sum + Number(kind === "delivery" ? row.totalProfit : row.totalPrice) || 0,
    0
  );
  const visibleTotal = visible.reduce(
    (sum, row) => sum + Number(kind === "delivery" ? row.totalProfit : row.totalPrice) || 0,
    0
  );
  const allVisibleSelected = visible.length > 0 && visible.every((row) => selected.includes(row._id));

  const toggle = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const toggleAll = () => {
    setSelected(allVisibleSelected ? [] : visible.map((row) => row._id));
  };

  const saveSettings = async (patch, notice) => {
    setError("");
    try {
      await api.put("/admin/platform/settings", {
        ...patch,
        paystackSecretKey: secret || undefined,
      });
      setSecret("");
      if (notice) setMessage(notice);
      await load();
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Could not save payment settings.");
    }
  };

  const retryRows = async (targets) => {
    if (!targets.length) return;
    setRunning(true);
    setError("");
    setMessage(`Paying ${targets.length} record${targets.length === 1 ? "" : "s"}.`);
    try {
      for (const row of targets) {
        await api.post("/admin/payments/retry", { kind, id: row._id });
        await load();
      }
      setMessage("Retry finished. Paid records have left this list.");
      setSelected([]);
    } catch (retryError) {
      setError(retryError.response?.data?.message || "Retry stopped.");
    } finally {
      setRunning(false);
      load().catch(() => {});
    }
  };

  const markSelected = async () => {
    if (!selectedRows.length) return;
    setRunning(true);
    setError("");
    try {
      const response = await api.post("/admin/payments/manual-bulk", {
        kind,
        ids: selectedRows.map((row) => row._id),
      });
      setMessage(`${response.data.data.paid.length} payment${response.data.data.paid.length === 1 ? "" : "s"} marked paid. Receipts are now visible.`);
      setSelected([]);
      await load();
    } catch (markError) {
      setError(markError.response?.data?.message || "Could not update the selected payments.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl bg-gradient-to-br from-green-800 to-green-950 p-6 text-white">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-green-100">Jhonks payouts</p>
        <h1 className="mt-2 text-3xl font-bold">Unpaid payments</h1>
        <p className="mt-2 max-w-2xl text-sm text-green-50">
          Filter the queue, select the rows you want, then retry Paystack or mark them paid together. Paid records leave this page and their receipts open in sale history.
        </p>
      </section>

      {error && <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
      {message && <p className="rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-800">{message}</p>}

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Automatic payment</h2>
          <p className="mt-1 text-sm text-gray-500">
            {settings?.paystackConfigured ? `Paystack key saved ${settings.paystackSecretKeyMasked}` : "Add the Paystack secret key before turning either switch on."}
          </p>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-gray-100 px-4 py-3">
            <div>
              <p className="font-semibold text-gray-900">Seller sales</p>
              <p className="mt-1 text-sm text-gray-500">New seller checkouts pay through Paystack.</p>
            </div>
            <button
              type="button"
              onClick={() =>
                saveSettings(
                  { sellerAutomaticPaymentEnabled: !settings?.sellerAutomaticPaymentEnabled },
                  settings?.sellerAutomaticPaymentEnabled
                    ? "Seller automatic payment is off. New sales wait here for a manual payment."
                    : "Seller automatic payment is on. New sales pay through Paystack."
                )
              }
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                settings?.sellerAutomaticPaymentEnabled ? "bg-green-700 text-white" : "bg-gray-200 text-gray-800"
              }`}
            >
              {settings?.sellerAutomaticPaymentEnabled ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-gray-100 px-4 py-3">
            <div>
              <p className="font-semibold text-gray-900">Agent deliveries</p>
              <p className="mt-1 text-sm text-gray-500">New agent deliveries pay through Paystack.</p>
            </div>
            <button
              type="button"
              onClick={() =>
                saveSettings(
                  { agentAutomaticPaymentEnabled: !settings?.agentAutomaticPaymentEnabled },
                  settings?.agentAutomaticPaymentEnabled
                    ? "Agent automatic payment is off. New deliveries wait here for a manual payment."
                    : "Agent automatic payment is on. New deliveries pay through Paystack."
                )
              }
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                settings?.agentAutomaticPaymentEnabled ? "bg-green-700 text-white" : "bg-gray-200 text-gray-800"
              }`}
            >
              {settings?.agentAutomaticPaymentEnabled ? "Enabled" : "Disabled"}
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
            placeholder="Paste Paystack secret key"
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
          />
          <button type="button" onClick={() => saveSettings({}, secret.trim() ? "Paystack key saved." : "")} className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white">
            Save key
          </button>
        </div>
      </section>

      <section className="space-y-4 rounded-3xl bg-white p-6 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {[
            ["sale", "Seller sales"],
            ["delivery", "Agent deliveries"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setKind(id);
                setRows([]);
              }}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${kind === id ? "bg-green-700 text-white" : "bg-gray-100 text-gray-700"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setPeriod(id)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold ${period === id ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex flex-wrap gap-3">
            <label className="text-sm text-gray-600">
              From
              <input type="date" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} className="mt-1 block rounded-xl border px-3 py-2" />
            </label>
            <label className="text-sm text-gray-600">
              To
              <input type="date" value={customTo} onChange={(event) => setCustomTo(event.target.value)} className="mt-1 block rounded-xl border px-3 py-2" />
            </label>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setPaymentStatus(id)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold ${paymentStatus === id ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, phone, or account"
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
          <p>{visible.length} showing · {money(visibleTotal)}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={running || !selectedRows.length}
              onClick={markSelected}
              className="rounded-xl bg-gray-900 px-4 py-2 font-semibold text-white disabled:opacity-50"
            >
              Mark {selectedRows.length || ""} selected paid
            </button>
            <button
              type="button"
              disabled={running || !(selectedRows.length || visible.length)}
              onClick={() => retryRows(selectedRows.length ? selectedRows : visible)}
              className="rounded-xl bg-green-700 px-4 py-2 font-semibold text-white disabled:opacity-50"
            >
              {running ? "Paying…" : selectedRows.length ? `Retry ${selectedRows.length} selected` : "Retry shown payments"}
            </button>
          </div>
        </div>
        {selectedRows.length > 0 && (
          <p className="rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-900">
            {selectedRows.length} selected · {money(selectedTotal)}. Marking them paid writes a receipt and removes them from this list.
          </p>
        )}
      </section>

      <div className="overflow-x-auto rounded-3xl bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select shown payments" />
              </th>
              <th className="px-4 py-3">Person</th>
              <th className="px-4 py-3">Account</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const person = kind === "delivery" ? row.agent : row.user;
              const bank = kind === "delivery" ? row.selectedPaymentBank : row.bankDetails;
              const amount = kind === "delivery" ? row.totalProfit : row.totalPrice;
              return (
                <tr key={row._id} className={`border-t border-gray-100 ${selected.includes(row._id) ? "bg-green-50" : ""}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.includes(row._id)} onChange={() => toggle(row._id)} aria-label={`Select ${personName(person)}`} />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{personName(person)}</p>
                    <p className="text-xs text-gray-500">{person?.phoneNumber || person?.email || ""}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <p>{bank?.accountName || "No account name"}</p>
                    <p>{bank?.accountNumber || "No account"} · {bank?.bankName || "No bank"}</p>
                    {row.paymentFailure && <p className="mt-1 text-xs text-red-700">{row.paymentFailure}</p>}
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{money(amount)}</td>
                  <td className="px-4 py-3 capitalize text-gray-700">{row.paymentStatus || "pending"}</td>
                  <td className="px-4 py-3 text-gray-500">{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!visible.length && <p className="px-6 py-10 text-sm text-gray-500">No payments match this filter.</p>}
      </div>
    </div>
  );
};

export default PaymentsAdmin;
